import React, { useState, useRef, useEffect } from 'react';
import { Box, Paper, Typography, FormHelperText } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

interface TimeSelectProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  /** "HH:MM" 形式。指定すると、この時刻以前のオプションをグレーアウト・選択不可にする */
  minTime?: string;
}

const MINUTE_OPTIONS = ['00', '15', '30', '45'];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) =>
  String(i).padStart(2, '0')
);

/** 指定した時（h）が minTime より前で選択不可かどうかを判定 */
const isHourDisabled = (h: string, minTime?: string): boolean => {
  if (!minTime) return false;
  const [minH, minM] = minTime.split(':');
  if (h < minH) return true;
  if (h === minH) {
    // この時に有効な分オプション（> minM）が1つもなければ無効
    return !MINUTE_OPTIONS.some((m) => m > minM);
  }
  return false;
};

/** 指定した分（m）と時（h）の組み合わせが minTime 以下で選択不可かどうかを判定 */
const isMinuteDisabled = (m: string, h: string, minTime?: string): boolean => {
  if (!minTime) return false;
  const [minH, minM] = minTime.split(':');
  if (h > minH) return false;
  if (h === minH) return m <= minM;
  return true;
};

export const TimeSelect: React.FC<TimeSelectProps> = ({
  value,
  onChange,
  label,
  required = false,
  error = false,
  helperText,
  disabled = false,
  minTime,
}) => {
  const [hourOpen, setHourOpen] = useState(false);
  const [minOpen,  setMinOpen]  = useState(false);
  const containerRef  = useRef<HTMLDivElement>(null);
  const hourMenuRef   = useRef<HTMLDivElement>(null);
  const minMenuRef    = useRef<HTMLDivElement>(null);

  const [hour, minute] = value ? value.split(':') : ['', ''];

  // ドロップダウンが開いたら選択済み行、未選択かつ minTime ありなら +1時間へスクロール
  useEffect(() => {
    if (hourOpen && hourMenuRef.current) {
      const sel = hourMenuRef.current.querySelector('[data-selected="true"]');
      if (sel) {
        sel.scrollIntoView({ block: 'center' });
      } else if (minTime) {
        const [minH] = minTime.split(':');
        const suggestH = String(Math.min(parseInt(minH, 10) + 1, 23)).padStart(2, '0');
        const suggested = hourMenuRef.current.querySelector(`[data-hour="${suggestH}"]`);
        if (suggested) suggested.scrollIntoView({ block: 'center' });
      }
    }
  }, [hourOpen, minTime]);

  useEffect(() => {
    if (minOpen && minMenuRef.current) {
      const sel = minMenuRef.current.querySelector('[data-selected="true"]');
      if (sel) sel.scrollIntoView({ block: 'center' });
    }
  }, [minOpen]);

  // コンポーネント外クリックで両方閉じる
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setHourOpen(false);
        setMinOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const handleHourSelect = (h: string) => {
    if (isHourDisabled(h, minTime)) return;

    let effectiveMinute = minute || '00';

    // 選択した時で現在の分が無効になる場合、最初の有効な分に自動修正
    if (minTime) {
      const [minH, minM] = minTime.split(':');
      if (h === minH && effectiveMinute <= minM) {
        effectiveMinute = MINUTE_OPTIONS.find((m) => m > minM) ?? '00';
      }
    }

    onChange(`${h}:${effectiveMinute}`);
    setHourOpen(false);
  };

  const handleMinSelect = (m: string) => {
    if (isMinuteDisabled(m, hour || '00', minTime)) return;
    onChange(`${hour || '00'}:${m}`);
    setMinOpen(false);
  };

  const triggerSx = (isOpen: boolean) => ({
    height: 52,
    border: `1.5px solid ${error ? '#d32f2f' : isOpen ? '#1976D2' : '#BDBDBD'}`,
    borderRadius: 2,
    px: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: disabled ? 'default' : 'pointer',
    bgcolor: 'background.paper',
    userSelect: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    boxShadow: isOpen ? '0 0 0 3px rgba(25,118,210,0.12)' : 'none',
    '&:hover': !disabled ? { borderColor: error ? '#d32f2f' : '#1976D2' } : {},
  });

  const menuItemSx = (isSelected: boolean, isDisabled: boolean) => ({
    px: 2,
    py: 1.5,
    fontSize: 16,
    cursor: isDisabled ? 'default' : 'pointer',
    bgcolor: isSelected && !isDisabled ? 'primary.main' : 'background.paper',
    color: isDisabled ? 'text.disabled' : isSelected ? '#fff' : 'text.primary',
    fontWeight: isSelected && !isDisabled ? 600 : 400,
    opacity: isDisabled ? 0.45 : 1,
    pointerEvents: isDisabled ? 'none' : 'auto',
    '&:hover': !isDisabled ? { bgcolor: isSelected ? 'primary.dark' : 'action.hover' } : {},
  });

  return (
    <Box ref={containerRef}>
      {label && (
        <Typography
          component="label"
          sx={{
            fontSize: '0.75rem',
            color: error ? '#d32f2f' : 'text.secondary',
            display: 'block',
            mb: 0.75,
          }}
        >
          {label}
          {required && (
            <Box component="span" sx={{ color: 'text.secondary', ml: 0.25 }}>
              *
            </Box>
          )}
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

        {/* 時 ドロップダウン */}
        <Box sx={{ flex: 1, position: 'relative' }}>
          <Box sx={triggerSx(hourOpen)} onClick={() => { if (!disabled) { setMinOpen(false); setHourOpen((v) => !v); } }}>
            <Typography sx={{ fontSize: 16, fontWeight: 500, color: hour ? 'text.primary' : 'text.disabled' }}>
              {hour || '--'}
            </Typography>
            <KeyboardArrowDownIcon
              sx={{
                color: 'text.secondary',
                fontSize: 20,
                transform: hourOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.15s',
              }}
            />
          </Box>

          {hourOpen && (
            <Paper
              ref={hourMenuRef}
              elevation={4}
              sx={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1300, maxHeight: 220, overflowY: 'auto', borderRadius: 2 }}
            >
              {HOUR_OPTIONS.map((h) => {
                const isDisabled = isHourDisabled(h, minTime);
                return (
                  <Box
                    key={h}
                    data-selected={h === hour}
                    data-hour={h}
                    onClick={() => handleHourSelect(h)}
                    sx={menuItemSx(h === hour, isDisabled)}
                  >
                    {h}
                  </Box>
                );
              })}
            </Paper>
          )}
        </Box>

        <Typography sx={{ color: 'text.secondary', fontSize: 16, flexShrink: 0 }}>時</Typography>

        {/* 分 ドロップダウン */}
        <Box sx={{ flex: 1, position: 'relative' }}>
          <Box sx={triggerSx(minOpen)} onClick={() => { if (!disabled) { setHourOpen(false); setMinOpen((v) => !v); } }}>
            <Typography sx={{ fontSize: 16, fontWeight: 500, color: minute ? 'text.primary' : 'text.disabled' }}>
              {minute || '--'}
            </Typography>
            <KeyboardArrowDownIcon
              sx={{
                color: 'text.secondary',
                fontSize: 20,
                transform: minOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.15s',
              }}
            />
          </Box>

          {minOpen && (
            <Paper
              ref={minMenuRef}
              elevation={4}
              sx={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1300, borderRadius: 2, overflow: 'hidden' }}
            >
              {MINUTE_OPTIONS.map((m) => {
                const isDisabled = isMinuteDisabled(m, hour || '00', minTime);
                return (
                  <Box
                    key={m}
                    data-selected={m === minute}
                    onClick={() => handleMinSelect(m)}
                    sx={menuItemSx(m === minute, isDisabled)}
                  >
                    {m}
                  </Box>
                );
              })}
            </Paper>
          )}
        </Box>

        <Typography sx={{ color: 'text.secondary', fontSize: 16, flexShrink: 0 }}>分</Typography>
      </Box>

      {helperText && (
        <FormHelperText error={error} sx={{ mx: 1.75, mt: 0.5 }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};
